import { scheduleInstant } from '../../schedule';
import useOfferImagePreview, { OfferImagePreviewStatus } from '../../useOfferImagePreview';
import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { OfferCard } from 'components';
import ImageUpload from './components/ImageUpload';
import OfferSaveActions from '../OfferSaveActions';
import WizardBackButton from '../WizardBackButton';
import api from 'utils/api';
import { getApiErrorMessage } from 'utils/apiErrors';
import { configureOfferPreviewCardProps } from '../OfferWizard/utils';
import TermsAndConditionsSuggestionsDialog, {
  appendTerms,
} from '../TermsAndConditionsSuggestionsDialog';

// URL validation regex - matching BusinessForm exactly
const urlPattern =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

const TERMS_AND_CONDITIONS_MAX_LENGTH = 1500;

const schema = yup.object().shape({
  headline1: yup.string().max(30, 'Headline 1 must be 30 characters or fewer.').required('Enter headline 1.'),
  headline2: yup.string().max(40, 'Headline 2 must be 40 characters or fewer.').required('Enter headline 2.'),
  description: yup.string().max(115, 'Description must be 115 characters or fewer.').required('Enter an offer description.'),
  siteUrl: yup
    .string()
    .matches(urlPattern, 'Enter a valid website URL, such as https://example.com.')
    .max(500, 'Website URL must be 500 characters or fewer.')
    .required('Enter a website URL.'),
  termsAndConditions: yup.string()
    .max(TERMS_AND_CONDITIONS_MAX_LENGTH, 'Terms and conditions must be 1,500 characters or fewer.')
    .required('Enter the terms and conditions for this offer.'),
});

const OfferDetails = ({
  back,
  onComplete,
  values,
  preserveLegacyImage = false,
  onSave,
  onUpdate,
  onValuesChange,
  saveButtonLabel,
}) => {
  const { logoUrl: businessLogo, businessId } = useSelector(state => get(state, 'business.companyInfo'));
  const [urlValidationError, setUrlValidationError] = useState(null);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [showTermSuggestions, setShowTermSuggestions] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    watch,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues: values.details || {
      headline1: '',
      headline2: '',
      description: '',
      offerImage: '',
      siteUrl: '',
      termsAndConditions: '',
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const currentValues = watch();
  const imagePreview = useOfferImagePreview(businessId, currentValues.offerImage, !preserveLegacyImage);

  useEffect(() => {
    const textFields = [
      'headline1',
      'headline2',
      'description',
      'siteUrl',
      'termsAndConditions',
    ].filter((fieldName) => touchedFields[fieldName]);

    if (textFields.length > 0) {
      void trigger(textFields);
    }
  }, [
    currentValues.headline1,
    currentValues.headline2,
    currentValues.description,
    currentValues.siteUrl,
    currentValues.termsAndConditions,
    touchedFields,
    trigger,
  ]);

  useEffect(() => {
    setUrlValidationError(null);
  }, [currentValues.siteUrl]);

  useEffect(() => {
    onValuesChange?.('details', currentValues, isDirty);
  }, [currentValues, isDirty, onValuesChange]);

  // Extract showPhone and showAddress from audience configuration
  // Show icons if at least one address has them enabled
  const showPhone = values?.audience?.local?.some(loc => loc.showPhone === true) || false;
  const showAddress = values?.audience?.local?.some(loc => loc.showAddress === true) || false;

  // Get register props for siteUrl and merge onBlur handlers
  const siteUrlRegister = register('siteUrl');
  const { onBlur: registerOnBlur, ...siteUrlRegisterProps } = siteUrlRegister;

  const validateUrlWithGoogleSafeBrowsing = async (url) => {
    if (!url || url.trim() === '') {
      setUrlValidationError(null);
      return true;
    }

    setIsValidatingUrl(true);
    setUrlValidationError(null);

    try {
      const response = await api.post('/common/validate-url', { url });
      
      if (response.status === 'BLOCK') {
        setUrlValidationError(response.message || 'This URL cannot be used for security reasons.');
        setIsValidatingUrl(false);
        return false;
      } else {
        setUrlValidationError(null);
        setIsValidatingUrl(false);
        return true;
      }
    } catch (error) {
      // Fail closed - block on API errors
      console.error('URL validation error:', error);
      setUrlValidationError(getApiErrorMessage(
        error,
        'We couldn’t verify this URL right now. Please try again.'
      ));
      setIsValidatingUrl(false);
      return false;
    }
  };

  const handleUrlBlur = async (e) => {
    const url = e.target.value;
    const isUrlSyntaxValid = await trigger('siteUrl');

    if (isUrlSyntaxValid && url && url.trim() !== '') {
      await validateUrlWithGoogleSafeBrowsing(url);
    } else {
      setUrlValidationError(null);
    }
  };

  const validateAndPrepareDetails = async (data) => {
    // Validate URL before submission
    const siteUrl = data.siteUrl;
    if (siteUrl && siteUrl.trim() !== '') {
      const isValid = await validateUrlWithGoogleSafeBrowsing(siteUrl);
      if (!isValid) {
        return null;
      }
    }

    return {
      ...data,
      offerImage: data.offerImage || '',
    };
  };

  const handleFormSubmit = async (data) => {
    const preparedDetails = await validateAndPrepareDetails(data);
    if (preparedDetails) onComplete(preparedDetails);
  };

  return (
    <>
      <Box mb={4}>
        <Typography variant="h6" component="h2">
          Let’s write your offer
        </Typography>
        <Typography variant="body2" component="p">
          Tell your audience about your business and your offer
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item lg={6} sm={12}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <FormControl fullWidth variant="outlined" error={!!errors.headline1}>
              <TextField
                label="Headline 1"
                {...register('headline1')}
                variant="outlined"
                margin="dense"
                error={!!errors.headline1}
              />
              <FormHelperText error>
                <Typography component="span" display="flex" justifyContent="space-between">
                  <span>{errors.headline1?.message}</span>
                  <Typography color="textSecondary" variant="caption">
                    {currentValues.headline1?.length || 0} / 30
                  </Typography>
                </Typography>
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth variant="outlined" error={!!errors.headline2}>
              <TextField
                label="Headline 2"
                {...register('headline2')}
                variant="outlined"
                margin="dense"
                error={!!errors.headline2}
              />
              <FormHelperText error>
                <Typography component="span" display="flex" justifyContent="space-between">
                  <span>{errors.headline2?.message}</span>
                  <Typography color="textSecondary" variant="caption">
                    {currentValues.headline2?.length || 0} / 40
                  </Typography>
                </Typography>
              </FormHelperText>
            </FormControl>

            <FormControl fullWidth variant="outlined" error={!!errors.description}>
              <TextField
                label="Description"
                {...register('description')}
                variant="outlined"
                margin="dense"
                multiline
                error={!!errors.description}
              />
              <FormHelperText error>
                <Typography component="span" display="flex" justifyContent="space-between">
                  <span>{errors.description?.message}</span>
                  <Typography color="textSecondary" variant="caption">
                    {currentValues.description?.length || 0} / 115
                  </Typography>
                </Typography>
              </FormHelperText>
            </FormControl>

            <ImageUpload 
              error={errors.offerImage} 
              control={control} 
              businessId={businessId}
              keywords={values.description?.keywords || []}
              headline1={currentValues.headline1}
              headline2={currentValues.headline2}
              description={currentValues.description}
              key={businessId}
              uploadUrl={`/api/offer/${businessId}/saveImage`}
              helperText="Drag and drop or upload an image to help users identify your offer. Choose an image up to 2 MB. We’ll optimize it before uploading."
            />

            <Box sx={{ mt: 2 }}>
            <FormControl fullWidth variant="outlined" error={!!errors.siteUrl || !!urlValidationError}>
              <TextField
                label="Site URL"
                {...siteUrlRegisterProps}
                onBlur={(e) => {
                  // Call react-hook-form's onBlur handler first
                  registerOnBlur(e);
                  // Then call our custom validation
                  handleUrlBlur(e);
                }}
                variant="outlined"
                margin="dense"
                multiline
                disabled={isValidatingUrl}
                helperText={isValidatingUrl ? 'Validating URL...' : undefined}
              />
              <FormHelperText error={!!errors.siteUrl || !!urlValidationError}>
                <Typography component="span" display="flex" justifyContent="space-between">
                  <span>{errors.siteUrl?.message || urlValidationError || ''}</span>
                  <Typography color="textSecondary" variant="caption">
                    {currentValues.siteUrl?.length || 0} / 500
                  </Typography>
                </Typography>
              </FormHelperText>
            </FormControl>
            </Box>
            <Typography variant="caption" color="textSecondary">
Provide your website URL. You can append the URL to pass needed data.            </Typography>
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="flex-end" mb={0.5}>
                <Button
                  size="small"
                  onClick={() => setShowTermSuggestions(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Browse T&amp;C
                </Button>
              </Box>
              <FormControl fullWidth variant="outlined" error={!!errors.termsAndConditions}>
                <TextField
                  label="Offer terms and conditions"
                  {...register('termsAndConditions')}
                  InputLabelProps={currentValues.termsAndConditions ? { shrink: true } : undefined}
                  variant="outlined"
                  margin="dense"
                  multiline
                />
                <FormHelperText error>
                  <Typography component="span" display="flex" justifyContent="space-between">
                    <span>{errors.termsAndConditions?.message}</span>
                    <Typography color="textSecondary" variant="caption">
                      {currentValues.termsAndConditions?.length || 0} / {TERMS_AND_CONDITIONS_MAX_LENGTH}
                    </Typography>
                  </Typography>
                </FormHelperText>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                Describe the terms for this offer or provide a link to terms on your site.
              </Typography>
            </Box>
          </form>
        </Grid>

        <Grid item sm={12} lg={6} xs={12}>
          <Box width="100%" position="sticky" top={0}>
            <Box mx="auto" maxWidth={450}>
              <Box textAlign="right">
                {/* <OfferListPreview
                  offer={{
                    ...currentValues,
                    endDate: scheduleInstant(get(values, 'budget.endDate')),
                    imageUrl: imagePreview.imageUrl,
                    businessImageUrl: businessLogo,
                  }}
                /> */}
              </Box>
              <OfferImagePreviewStatus {...imagePreview} />
              <OfferCard
                {...currentValues}
                {...configureOfferPreviewCardProps}
                endDate={scheduleInstant(get(values, 'budget.endDate'))}
                imageUrl={imagePreview.imageUrl}
                businessImageUrl={businessLogo}
                isPreview
                showPhone={showPhone}
                showAddress={showAddress}
                offerLocation={values.audience}
                claimsRemaining={values.budget?.maxClick}
                unlimitedClaims={values.budget?.unlimitedClaims === true}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box mt={2} mb={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <OfferSaveActions
          onUpdate={onUpdate
            ? handleSubmit(async (data) => {
                const preparedDetails = await validateAndPrepareDetails(data);
                if (preparedDetails) onUpdate({ details: preparedDetails });
              })
            : undefined}
          onSave={() => onSave({ details: getValues() })}
          saveButtonLabel={saveButtonLabel}
        />
        <Box>
          <WizardBackButton onClick={back} />
          <Button color="primary" type="submit" onClick={handleSubmit(handleFormSubmit)} variant="contained">
            Next
          </Button>
        </Box>
      </Box>

      <TermsAndConditionsSuggestionsDialog
        open={showTermSuggestions}
        onClose={() => setShowTermSuggestions(false)}
        offer={{
          fulfillment: values?.description?.fulfillment,
        }}
        existingTerms={currentValues.termsAndConditions}
        allowCustomTerm
        onApply={(terms) => {
          setValue(
            'termsAndConditions',
            appendTerms(getValues('termsAndConditions'), terms),
            { shouldDirty: true, shouldValidate: true }
          );
        }}
      />
    </>
  );
};

export default OfferDetails;
